import { useState } from 'react'
import styled, { useTheme } from 'styled-components'
import truncate from 'lodash/truncate'

import TrayModal from 'components/modals/TrayModal'
import InvisibleButton from 'components/display/InvisibleButton'
import LazyImage from 'components/display/LazyImage'
import { HeadingMd } from 'components/display/Typography'
import { formatPrice } from 'components/utils/pricing'

import LinkOrA from '../LinkOrA'
import { Location, Title } from '../styled'

import AvailabilityRange from './AvailabilityRange'
import { rebuildUrlWithSearchParamsForDates } from './utils'

const Pill = styled.div`
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xxs']?.['font-size']?.value};
  font-weight: 400;
  color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  background-color: ${({ theme }) => theme?.['-ds-success-100']?.value};
  border-radius: 100px;
  padding: 8px 14px;
  width: fit-content;
`

const LargePill = styled(Pill)`
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  font-weight: 500;
  padding: 16px;
  border-radius: 8px;
  width: 100%;

  display: flex;
  align-items: center;
`

type AvailableDatesProps = {
  availableDates: { startDate: string; endDate: string; total: number }[]
  linkOptions: {
    url: string
    asLink: boolean
    sameTab?: boolean
  }
  thumbnail: string
  listingTitle: string
  locationName: string
  capacity: number
}

const AvailableDates = ({
  availableDates,
  linkOptions,
  thumbnail,
  listingTitle,
  locationName,
  capacity,
}: AvailableDatesProps) => {
  const [modalOpen, setModalOpen] = useState(false)
  const theme = useTheme()

  if (!availableDates || availableDates?.length === 0) {
    return null
  }

  const { url, asLink, sameTab } = linkOptions

  const firstDateUrl = rebuildUrlWithSearchParamsForDates(
    url,
    availableDates[0].startDate,
    availableDates[0].endDate,
  )

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <LinkOrA to={firstDateUrl} asLink={asLink} sameTab={sameTab}>
          <Pill>
            <AvailabilityRange
              startDate={availableDates[0].startDate}
              endDate={availableDates[0].endDate}
            />
          </Pill>
        </LinkOrA>
        {availableDates.length > 1 && (
          <InvisibleButton
            aria-label="See all available weekends"
            onClick={() => setModalOpen(true)}
          >
            <Pill style={{ marginLeft: '8px' }}>
              + {availableDates.length - 1}
            </Pill>
          </InvisibleButton>
        )}
      </div>

      {availableDates?.length > 1 && (
        <TrayModal
          onRequestClose={() => setModalOpen(false)}
          isOpen={modalOpen}
        >
          <div style={{ paddingBottom: '16px' }}>
            <HeadingMd style={{ marginTop: '16px' }}>
              Available weekends for:
            </HeadingMd>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <LazyImage
                data-src={thumbnail}
                alt={listingTitle}
                rounded
                height={100}
                width={100}
                style={{ width: '100px', height: '100px', marginRight: '16px' }}
              />
              <div>
                <Title compact>{truncate(listingTitle, { length: 65 })}</Title>
                <Location compact>
                  {`${locationName} • `}
                  Sleeps&nbsp;{capacity}
                </Location>
              </div>
            </div>

            {availableDates?.map(({ startDate, endDate, total }) => {
              const newUrl = rebuildUrlWithSearchParamsForDates(
                url,
                startDate,
                endDate,
              )

              return (
                <LinkOrA
                  to={newUrl}
                  asLink={asLink}
                  sameTab={sameTab}
                  key={[startDate, endDate, total].join('--')}
                >
                  <LargePill style={{ marginTop: '16px' }}>
                    <AvailabilityRange
                      startDate={startDate}
                      endDate={endDate}
                    />
                    <div style={{ marginLeft: 'auto' }}>
                      <p style={{ margin: 0 }}>{formatPrice(total, 0)}</p>
                      <p
                        style={{
                          margin: 0,
                          color: theme?.['-ds-neutral-500']?.value,
                          fontSize:
                            theme?.['-ds-font-size-xxs']?.['font-size']?.value,
                        }}
                      >
                        total
                      </p>
                    </div>
                  </LargePill>
                </LinkOrA>
              )
            })}
          </div>
        </TrayModal>
      )}
    </>
  )
}

export default AvailableDates
